import { useState } from "react";
import { Download, FileUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, API } from "../lib/api";
import { Button } from "../components/ui/button";

export default function CsvImport() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/listings/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "broward_local_import_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runImport = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/listings/import", fd);
      setResult(r.data);
      if (r.data.errors.length === 0) toast.success(`${r.data.imported} businesses imported`);
      else toast.warning(`${r.data.imported} imported, ${r.data.errors.length} rows had errors`);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <div data-testid="csv-import-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#1B2A4A]">CSV Bulk Import</h1>
        <p className="text-sm text-[#475569] mt-1">Seed multiple business listings at once</p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="text-sm text-[#475569] max-w-md leading-relaxed">
            Upload a CSV with columns: <code className="text-xs bg-[#F5F7FA] px-1.5 py-0.5 rounded border border-[#E2E8F0]">name, category, subcategory, address, city, zip, phone, email, website, description, service_cities, hours, status</code>.
            Categories must match the master list. Separate multiple categories, subcategories, and service cities with semicolons — e.g. a multiservice business can have <code className="text-xs bg-[#F5F7FA] px-1.5 py-0.5 rounded border border-[#E2E8F0]">Home Repair &amp; Maintenance;Cleaning &amp; Organization</code>.
          </div>
          <Button variant="outline" data-testid="download-template-button" onClick={downloadTemplate} className="border-[#E2E8F0]">
            <Download size={15} className="mr-2" /> Sample Template
          </Button>
        </div>

        <label
          htmlFor="csv-file"
          data-testid="csv-dropzone"
          className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#E2E8F0] rounded-xl py-10 cursor-pointer hover:border-[#2E7BD6]/50 hover:bg-[#F8FAFC] transition-colors"
        >
          <FileUp size={28} className="text-[#2E7BD6]" />
          <span className="text-sm font-medium text-[#0F172A]">{file ? file.name : "Click to choose a CSV file"}</span>
          <span className="text-xs text-[#94A3B8]">.csv only</span>
          <input id="csv-file" data-testid="csv-file-input" type="file" accept=".csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
        </label>

        <Button data-testid="run-import-button" onClick={runImport} disabled={!file || loading} className="bg-[#F5961B] hover:bg-[#dd8512] text-white w-full h-10">
          {loading ? "Importing..." : "Run Import"}
        </Button>
      </div>

      {result && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-4" data-testid="import-result">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={18} className="text-[#059669]" />
            <span className="font-semibold text-[#0F172A]" data-testid="import-count">{result.imported} businesses imported</span>
          </div>
          {result.errors.length > 0 && (
            <div className="space-y-2" data-testid="import-errors">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#B45309]">
                <AlertTriangle size={15} /> {result.errors.length} row(s) skipped
              </div>
              {result.errors.map((e, i) => (
                <div key={i} className="text-sm text-[#475569] bg-[#FEF3C7] border border-[#FDE68A] rounded-lg px-3 py-2" data-testid={`import-error-${i}`}>
                  Row {e.row}{e.name ? ` (${e.name})` : ""}: {e.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
